import { JSONContent } from '@tiptap/core';
import { NodeViewWrapper } from '@tiptap/react';
import {
  CustomSectionAttributes,
  CustomSectionType,
  CustomSectionContentState,
} from 'api/custom-section/types';
import { streamTipTapNodes } from 'helpers/stream';
import { ComponentType, useCallback, useState } from 'react';
import { LoaderStreamingComponent } from 'components/common/loader-streaming';
import { isContentEditable } from 'components/organisms/document-editor/extensions/custom-section/utils';
import {
  CanBeResolvedOptions,
  CustomSectionComponentProps,
  CustomSectionNodeOptions,
  CustomSectionViewProps,
} from '../types';
import * as AccountDetailsSection from './sections-type/account-details';
import * as ActionSection from './sections-type/action-section';
import {
  CompanyPageSourceDerivedSectionGenerated,
  CompanyPageSourceDerivedSectionPrompt,
} from './sections-type/company-page-source-derived';
import * as EmailDetailsSection from './sections-type/email-details';
import * as ExtractInfoSection from './sections-type/extract-info';
import * as MeetingDetailsSection from './sections-type/meeting-details';
import * as MultiQuestionSection from './sections-type/multi-question';
import {
  PeoplePageSourceDerivedQuestionGeneratedSection,
  PeoplePageSourceDerivedQuestionSectionPrompt,
} from './sections-type/people-page-source-derived-question';
import { PeoplePageSourceDerivedSignalGeneratedSection } from './sections-type/people-page-source-derived-signal/generated';
import { PeoplePageSourceDerivedSignalSectionPrompt } from './sections-type/people-page-source-derived-signal/prompt';
import * as QuestionAnswerItemSection from './sections-type/question-answer-item';
import * as QuestionSection from './sections-type/question-sections';
import * as RecordBlockSection from './sections-type/record-block';
import * as SignalSection from './sections-type/signal-section';
import * as TableDetailsSection from './sections-type/table-details';
import {
  TextBlockSectionGenerated,
  TextBlockSectionPrompt,
} from './sections-type/text-block';

const SECTION_COMPONENTS = {
  [CustomSectionType.Question]: {
    prompt: QuestionSection.QuestionPromptSection,
    generated: QuestionSection.QuestionGeneratedSection,
  },
  [CustomSectionType.CompanyPageSourceDerivedSection]: {
    prompt: CompanyPageSourceDerivedSectionPrompt,
    generated: CompanyPageSourceDerivedSectionGenerated,
  },
  [CustomSectionType.Action]: {
    prompt: ActionSection.ActionPromptSection,
    generated: ActionSection.ActionGeneratedSection,
  },
  [CustomSectionType.ExtractInfo]: {
    prompt: ExtractInfoSection.ExtractInfoPromptSection,
    generated: ExtractInfoSection.ExtractInfoGeneratedSection,
  },
  [CustomSectionType.SignalDetails]: {
    prompt: SignalSection.SignalPromptSection,
    generated: SignalSection.SignalGeneratedSection,
  },
  [CustomSectionType.MeetingDetails]: {
    prompt: MeetingDetailsSection.MeetingDetailsPromptSection,
    generated: MeetingDetailsSection.MeetingDetailsGeneratedSection,
  },
  [CustomSectionType.AccountDetails]: {
    prompt: AccountDetailsSection.AccountDetailsPromptSection,
    generated: AccountDetailsSection.AccountDetailsGeneratedSection,
  },
  [CustomSectionType.CustomTextSection]: {
    prompt: TextBlockSectionPrompt,
    generated: TextBlockSectionGenerated,
  },
  [CustomSectionType.PeoplePageSourceDerivedQuestionSection]: {
    prompt: PeoplePageSourceDerivedQuestionSectionPrompt,
    generated: PeoplePageSourceDerivedQuestionGeneratedSection,
  },
  [CustomSectionType.PeoplePageSourceDerivedSignalSection]: {
    prompt: PeoplePageSourceDerivedSignalSectionPrompt,
    generated: PeoplePageSourceDerivedSignalGeneratedSection,
  },
  [CustomSectionType.EmailDetails]: {
    prompt: EmailDetailsSection.EmailDetailsPromptSection,
    generated: EmailDetailsSection.EmailDetailsGeneratedSection,
  },
  [CustomSectionType.MultiQuestion]: {
    prompt: MultiQuestionSection.MultiQuestionPromptSection,
    generated: MultiQuestionSection.MultiQuestionGeneratedSection,
  },
  [CustomSectionType.QuestionAnswerItem]: {
    prompt: QuestionAnswerItemSection.QuestionAnswerItemPromptSection,
    generated: QuestionAnswerItemSection.QuestionAnswerItemGeneratedSection,
  },
  [CustomSectionType.TableDetails]: {
    prompt: TableDetailsSection.TableDetailsPromptSection,
    generated: TableDetailsSection.TableDetailsGeneratedSection,
  },
  [CustomSectionType.RecordBlock]: {
    prompt: RecordBlockSection.RecordBlockPromptSection,
    generated: RecordBlockSection.RecordBlockGeneratedSection,
  },
};

export const CustomNodeView = ({
  node,
  updateAttributes,
  editor,
  getPos,
  extension,
  ...rest
}: CustomSectionViewProps) => {
  const [isStreaming, setIsStreaming] = useState(false);
  const options = extension.options as CustomSectionNodeOptions;
  const sectionType = node.attrs.sectionType;
  const isPrompt = node.attrs.contentState === CustomSectionContentState.Prompt;
  const Component = (options.customSectionRenderer?.(node) ??
    (isPrompt
      ? SECTION_COMPONENTS[sectionType].prompt
      : SECTION_COMPONENTS[sectionType]
          .generated)) as ComponentType<CustomSectionComponentProps>;

  const handleUpdate = (newAttrs: any) => {
    updateAttributes(newAttrs);
  };

  const handleSectionClick = useCallback(() => {
    const sectionId =
      node.attrs.templateSectionId ?? node.attrs.documentSectionId;
    const sectionMenuConfig = (
      editor.extensionManager.extensions.find(
        (ext) => ext.name === 'sectionMenu',
      ) as any
    )?.options;

    if (sectionMenuConfig?.onSectionClick && sectionId) {
      sectionMenuConfig.onSectionClick({
        id: sectionId,
        ...node.attrs,
        type: node.type.name,
      });
    }
  }, [editor, node.attrs, node.type.name]);

  const openSection = useCallback(() => {
    const nodePos = getPos();

    if (nodePos !== undefined && nodePos !== null) {
      editor
        .chain()
        .openCustomSection({
          nodeAt: nodePos,
        })
        .run();
    }
  }, [editor, getPos]);

  const undoSection = useCallback(() => {
    const nodePos = getPos();

    if (nodePos !== undefined && nodePos !== null) {
      editor
        .chain()
        .undoCustomSection({
          nodeAt: nodePos,
        })
        .run();
    }
  }, [editor, getPos]);

  const removeSection = useCallback(() => {
    const nodePos = getPos();

    if (nodePos !== undefined && nodePos !== null) {
      editor
        .chain()
        .removeCustomSection({
          nodeAt: nodePos,
        })
        .run();
    }
  }, [editor, getPos]);

  const updateContent = useCallback(
    (content: any, newAttrs?: any) => {
      const pos = getPos();

      // Handle both single node and array of nodes
      let contentNodes;

      if (Array.isArray(content)) {
        // If content is an array, create nodes from each item
        contentNodes = content.map((item) => editor.schema.nodeFromJSON(item));
      } else {
        // If content is a single node
        contentNodes = [editor.schema.nodeFromJSON(content)];
      }

      // Merge existing attributes with new attributes
      const updatedAttrs = { ...node.attrs, ...newAttrs };

      const newNode = node.type.create(
        updatedAttrs, // Use updated attributes instead of just existing ones
        contentNodes, // New content array
      );

      if (pos !== undefined && pos !== null) {
        const tr = editor.state.tr.replaceWith(
          pos,
          pos + node.nodeSize,
          newNode,
        );

        editor.view.dispatch(tr);
      }
    },
    [editor, getPos, node.attrs, node.nodeSize, node.type],
  );

  const canResolve = useCallback(
    (
      nodeOptions: CustomSectionNodeOptions,
    ): nodeOptions is CanBeResolvedOptions => {
      return nodeOptions.allowResolve === true;
    },
    [],
  );

  const onResolve = useCallback(
    (
      attributes: CustomSectionAttributes,
      payload: JSONContent | undefined,
    ): Promise<void> => {
      return new Promise((resolve) => {
        if (canResolve(options)) {
          options
            .resolver(attributes, payload?.content ?? [])
            .then(async (data: any) => {
              try {
                setIsStreaming(true);
                const nodePos = getPos();

                if (nodePos === null || nodePos === undefined) {
                  return;
                }

                const newNode = editor.schema.nodeFromJSON({
                  ...data.finalContent,
                  attrs: {
                    ...data.finalContent.attrs,
                    editable: false,
                    isTableStatic: true,
                  },
                });

                const replaced = editor
                  .chain()
                  .focus()
                  .command(({ tr }) => {
                    const current = tr.doc.nodeAt(nodePos);

                    if (!current) {
                      return false;
                    }

                    tr.replaceWith(
                      nodePos,
                      nodePos + current.nodeSize,
                      newNode,
                    );

                    return true;
                  })
                  .run();

                if (!replaced) {
                  setIsStreaming(false);

                  return;
                }

                await streamTipTapNodes({
                  editor,
                  identifierType: 'documentSectionId',
                  identifierValues: [
                    data.finalContent?.attrs?.documentSectionId,
                  ],
                  token: data.streamingMeta?.token,
                  channelName: data.streamingMeta?.channelName,
                  onStart: () => {
                    setIsStreaming(false);
                  },
                });
              } catch {
                setIsStreaming(false);
              }
            })
            .catch(() => {
              setIsStreaming(false);
              resolve();
            });
        } else {
          updateAttributes(attributes);
          resolve();
        }
      });
    },
    [canResolve, options, editor, getPos, updateAttributes],
  );

  return (
    <NodeViewWrapper
      data-rl-node
      data-section-id={
        node.attrs.templateSectionId ?? node.attrs.documentSectionId
      }
      contentEditable={
        isContentEditable(node) && editor.isEditable && node.attrs.editable
      }
      className={`custom-section-content ${node.attrs.contentState} ${node.attrs.sectionType}`}
      // TODO: have some unique identifier for the node
      key={node.attrs.templateSectionId ?? node.attrs.documentSectionId}
      onClick={handleSectionClick}
    >
      {isStreaming ? (
        <LoaderStreamingComponent />
      ) : (
        <Component
          {...rest}
          updateAttributes={handleUpdate}
          updateContent={updateContent}
          node={node}
          editor={editor}
          getPos={getPos}
          onRemove={removeSection}
          onDone={onResolve}
          onOpen={openSection}
          onUndo={undoSection}
          extension={extension}
          contentEditable={editor.isEditable}
          allowResolve={options.allowResolve}
          companyPageDerivedSectionSourcesUpdater={
            options.companyPageDerivedSectionSourcesUpdater
          }
          peoplePageDerivedSectionSourcesUpdater={
            options.peoplePageDerivedSectionSourcesUpdater
          }
          pageType={options.pageType}
          pageTriggerListFilter={options.pageTriggerListFilter}
        />
      )}
    </NodeViewWrapper>
  );
};
